import type { RouteRecordRaw } from 'vue-router';
import router from '@/router/generated_router';
import Layout from '@/views/Layout/Layout.vue';
import encrypt from '@/utils/crypto-util';

// 后端菜单数据接口
export interface BackendMenu {
    id: string | number;
    path: string;
    name?: string;
    component?: string;
    redirect?: string;
    meta?: {
        title?: string;
        icon?: string;
        roles?: string[];
        permission?: string;
        hidden?: boolean;
        keepAlive?: boolean;
        order?: number;
    };
    children?: BackendMenu[];
}

// ✅ 使用 Vite 的 Glob Import 自动生成组件映射表
const modules = import.meta.glob('@/views/**/*.vue');

// 转换为更友好的路径映射
const componentMap: Record<string, any> = {};

// 自动生成路径映射
Object.keys(modules).forEach((path) => {
    // path 例如: '@/views/dashboard/data.vue'
    // 转换为: '/dashboard/data'
    const routePath = path
        .replace('@/views', '') // 移除前缀
        .replace('.vue', ''); // 移除后缀

    componentMap[routePath] = modules[path];
});

console.log('自动生成的组件映射表:', componentMap);

// 🔍 调试：检查特定路径是否存在
console.log(
    '检查 /dashboard/data 是否存在:',
    componentMap['/dashboard/data'] ? '✅ 存在' : '❌ 不存在'
);
console.log('所有可用路径:', Object.keys(componentMap));
/**
 * 动态路由管理类
 */
export class DynamicRouteManager {
    private addedRoutes: string[] = []; // 记录已添加的路由名称
    private isInitialized = false; // 标记是否已初始化

    /**
     * 将后端菜单数据转换为Vue Router路由
     */
    private transformMenus(backendMenus: BackendMenu[]): RouteRecordRaw[] {
        return backendMenus.map((menu) => this.transformSingleMenu(menu));
    }

    /**
     * 转换单个菜单为路由
     */
    private transformSingleMenu(backendMenu: any): any {
        console.log(backendMenu, '转换单个菜单为路由');
        const vueRoute: any = {
            path: backendMenu.value,
            name: backendMenu.name,
            meta: {
                title: backendMenu.name,
            },
        };
        if (backendMenu.pageList && backendMenu.pageList.length > 0) {
            console.log('父级', backendMenu.value, backendMenu.name);

            //父级默认设置为Layout组件
            vueRoute.component = () => import('../views/Layout/Layout.vue');
            vueRoute.redirect = backendMenu.pageList[0].value;
            if (backendMenu.pageList && backendMenu.pageList.length > 0) {
                // 处理子菜单
                console.log('拥有子级', backendMenu.pageList);
                vueRoute.children = this.transformMenus(backendMenu.pageList);
            }
        } else if (!backendMenu.pageList) {
            console.log(
                '当前处理子路由',
                backendMenu.value,
                '从组件映射表加载',
                componentMap
            );
            // if(){

            // }

            // 安全的组件加载
            // const component = componentMap[backendMenu.value]
            let component: any = null;
            let role: any = null;
            for (const key in componentMap) {
                console.log('当前组件映射表', key);
                if (key.includes(backendMenu.value)) {
                    console.log('找到组件', key);
                    component = componentMap[key];
                    role = backendMenu.opList
                    break;
                }
            }

            if (component) {
                vueRoute.component = component;
                vueRoute.meta = { role, ...vueRoute.meta };
                console.log('✅ 组件加载成功:', backendMenu.value);
            } else {
                console.error('❌ 组件未找到:', backendMenu.value);
                console.log('可用的组件路径:', Object.keys(componentMap));

                // 尝试其他可能的路径格式
                const alternativePaths = [
                    backendMenu.value.replace(/^\//, ''), // 移除开头的斜杠
                    `/${backendMenu.value}`, // 添加开头的斜杠
                    backendMenu.value.toLowerCase(), // 转小写
                    backendMenu.value.replace(/\//g, '-'), // 替换斜杠为短横线
                ];

                let found = false;
                for (const altPath of alternativePaths) {
                    if (componentMap[altPath]) {
                        console.log('✅ 使用替代路径:', altPath);
                        vueRoute.component = componentMap[altPath];
                        found = true;
                        break;
                    }
                }

                if (!found) {
                    // 使用404组件作为fallback
                    vueRoute.component = () => import('@/views/NotFound/NotFound.vue');
                }
            }
        } else {
            // console.warn(`组件 ${backendMenu.component} 未在componentMap中找到`)
            // 设置为404组件作为fallback
            vueRoute.component = () => import('@/views/NotFound/NotFound.vue');
        }

        return vueRoute;
    }

    /**
     * 合并菜单路由到现有路由器
     */
    public mergeMenuRoutes(backendMenus: BackendMenu[]): void {
        console.log('开始合并动态菜单路由:', backendMenus);

        try {
            // 转换后端菜单数据为Vue Router格式
            const vueRoutes = this.transformMenus(backendMenus);

            // 添加路由到路由器
            vueRoutes.forEach((route) => {
                router.addRoute(route);
                this.addedRoutes.push(route.name as string);
                console.log(`已添加动态路由: ${route.path} (${route.name})`);
            });

            this.isInitialized = true;
            console.log(
                '动态菜单路由合并完成，共添加',
                vueRoutes.length,
                '个路由',
                vueRoutes,
                '当前列表'
            );

            // 保存已添加的菜单信息到localStorage，方便页面刷新后恢复
            localStorage.setItem('dynamicMenus', encrypt.aesEncrypty(JSON.stringify(backendMenus)));
        } catch (error) {
            console.error('动态菜单路由合并失败:', error);
            throw error;
        }
    }

    /**
     * 从localStorage恢复动态路由（页面刷新时使用）
     */
    public restoreMenuRoutesFromStorage(): void {
        console.log('🔄 开始恢复动态路由...');
        console.log('当前初始化状态:', this.isInitialized);
        console.log('已添加的路由:', this.addedRoutes);

        if (this.isInitialized) {
            console.log('⚠️ 动态路由已初始化，跳过恢复');
            return;
        }

        try {
            const storedMenus = localStorage.getItem('dynamicMenus');
            console.log('从localStorage获取的菜单数据:', storedMenus);

            if (storedMenus) {
                const backendMenus = JSON.parse(encrypt.aesDecrypt(storedMenus)) as BackendMenu[];
                console.log('解析后的菜单数据:', backendMenus);

                // 确保组件映射表已经生成
                console.log('当前组件映射表状态:', Object.keys(componentMap));

                // 🚨 应急方案：如果组件映射表为空，强制重新生成
                if (Object.keys(componentMap).length === 0) {
                    console.warn('⚠️ 组件映射表为空，使用应急方案');
                    this.generateEmergencyComponentMap(backendMenus);
                }

                this.mergeMenuRoutes(backendMenus);
                console.log('✅ 从localStorage恢复动态菜单路由成功');

                // 验证路由是否真的被添加了
                const currentRoutes = router.getRoutes();
                console.log(
                    '当前路由器中的所有路由:',
                    currentRoutes.map((r) => `${r.path} (${r.name})`)
                );
            } else {
                console.log('⚠️ localStorage中没有找到动态菜单数据');
            }
        } catch (error) {
            console.error('❌ 从localStorage恢复动态菜单路由失败:', error);
            // 清除可能损坏的数据
            localStorage.removeItem('dynamicMenus');
        }
    }

    /**
     * 应急方案：手动生成组件映射
     */
    private generateEmergencyComponentMap(menus: any[]) {
        console.log('🚨 生成应急组件映射表');

        const addToMap = (menu: any) => {
            if (menu.value) {
                // 尝试多种路径格式
                const possiblePaths = [
                    menu.value, // 原始路径
                    menu.value.replace(/^\//, ''), // 移除开头斜杠
                    `/${menu.value}`, // 添加开头斜杠
                ];

                possiblePaths.forEach((path) => {
                    try {
                        // 生成动态导入函数
                        const importPath = `@/views${path}.vue`;
                        console.log(`添加应急映射: ${path} -> ${importPath}`);
                        componentMap[path] = () => import(/* @vite-ignore */ importPath);
                    } catch (error) {
                        console.warn(`应急映射失败: ${path}`, error);
                    }
                });
            }

            // 递归处理子菜单
            if (menu.pageList && menu.pageList.length > 0) {
                menu.pageList.forEach(addToMap);
            }
        };

        menus.forEach(addToMap);
        console.log('应急组件映射表生成完成:', Object.keys(componentMap));
    }

    /**
     * 清除所有动态路由
     */
    public clearDynamicMenuRoutes(): void {
        this.addedRoutes.forEach((routeName) => {
            if (router.hasRoute(routeName)) {
                router.removeRoute(routeName);
                console.log(`已移除动态路由: ${routeName}`);
            }
        });

        this.addedRoutes = [];
        this.isInitialized = false;
        localStorage.removeItem('dynamicMenus');
        console.log('动态菜单路由清除完成');
    }

    /**
     * 获取已添加的路由列表
     */
    public getAddedRoutes(): string[] {
        return [...this.addedRoutes];
    }

    /**
     * 检查是否已初始化
     */
    public isRouteInitialized(): boolean {
        return this.isInitialized;
    }

    /**
     * 根据菜单数据生成导航菜单结构（用于侧边栏显示）
     */
    public generateNavigationMenu(backendMenus: BackendMenu[]): BackendMenu[] {
        return backendMenus
            .filter((menu) => !menu.meta?.hidden) // 过滤隐藏的菜单项
            .sort((a, b) => (a.meta?.order || 0) - (b.meta?.order || 0)) // 按order排序
            .map((menu) => ({
                ...menu,
                children: menu.children
                    ? this.generateNavigationMenu(menu.children)
                    : undefined,
            }));
    }
}

// 创建全局实例
export const dynamicRouteManager = new DynamicRouteManager();

/**
 * 便捷的合并菜单路由函数
 */
export function mergeUserMenuRoutes(backendMenus: BackendMenu[]): void {
    dynamicRouteManager.mergeMenuRoutes(backendMenus);
}

/**
 * 便捷的恢复路由函数
 */
export function restoreUserMenuRoutes(): void {
    dynamicRouteManager.restoreMenuRoutesFromStorage();
}

/**
 * 便捷的清除路由函数
 */
export function clearUserMenuRoutes(): void {
    dynamicRouteManager.clearDynamicMenuRoutes();
}

/**
 * 获取导航菜单数据
 */
export function getNavigationMenus(): BackendMenu[] {
    try {
        const storedMenus = localStorage.getItem('dynamicMenus');
        if (storedMenus) {
            const backendMenus = JSON.parse(encrypt.aesDecrypt(storedMenus)) as BackendMenu[];
            return dynamicRouteManager.generateNavigationMenu(backendMenus);
        }
    } catch (error) {
        console.error('获取导航菜单失败:', error);
    }
    return [];
}

/**
 * 🛠️ 调试工具：手动强制恢复路由
 */
export function forceRestoreRoutes() {
    console.log('🔧 手动强制恢复路由...');
    try {
        // 重置初始化状态
        dynamicRouteManager['isInitialized'] = false;
        dynamicRouteManager['addedRoutes'] = [];

        // 强制恢复
        dynamicRouteManager.restoreMenuRoutesFromStorage();

        console.log('✅ 手动恢复完成');
        return true;
    } catch (error) {
        console.error('❌ 手动恢复失败:', error);
        return false;
    }
}

/**
 * 🔍 调试工具：检查路由状态
 */
export function debugRouteStatus() {
    console.group('🔍 路由状态检查');

    // 检查localStorage
    console.log('📦 localStorage状态:');
    console.log('- token:', localStorage.getItem('usetoken') ? '✅' : '❌');
    console.log(
        '- dynamicMenus:',
        localStorage.getItem('dynamicMenus') ? '✅' : '❌'
    );

    // 检查组件映射表
    console.log('🗺️ 组件映射表:');
    console.log('- 总数:', Object.keys(componentMap).length);
    console.log(
        '- 包含 /dashboard/data:',
        componentMap['/dashboard/data'] ? '✅' : '❌'
    );
    console.log('- 所有路径:', Object.keys(componentMap));

    // 检查路由器
    const routes = router.getRoutes();
    console.log('🛣️ 路由器状态:');
    console.log('- 总路由数:', routes.length);
    console.log(
        '- 动态路由:',
        routes
            .filter((r) => !r.path.match(/^\/(Login|404)/))
            .map((r) => `${r.path} (${r.name})`)
    );

    console.groupEnd();
}

// 暴露到全局供调试使用
// if (typeof window !== 'undefined') {
//     (window as any).forceRestoreRoutes = forceRestoreRoutes
//     (window as any).debugRouteStatus = debugRouteStatus
// }
